import parsonsLogo from "@/assets/parsons-logo.png";
import fitLogo from "@/assets/fit-logo.png";
import academyArtsLogo from "@/assets/academy-arts-logo.png";
import scadLogo from "@/assets/scad-logo.png";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import type { ComponentType, ReactNode } from "react";
import DeconstructStep from "./DeconstructStep";
import StylistAnalysisStep from "./StylistAnalysisStep";
import StyleGuidanceStep from "./StyleGuidanceStep";

interface Step {
  title: string;
  body: string;
  Visual: ComponentType<{ autoplay?: boolean }>;
}

const STEPS: Step[] = [
  {
    title: "Digitize & Deconstruct",
    body: "We use computer vision to digitize and analyze your wardrobe in under 30 minutes. On average we scan 70 items.",
    Visual: DeconstructStep,
  },
  {
    title: "Identify & Fill Gaps",
    body: "Expert stylists audit your closet to identify critical gaps, mapping out exactly how to build a modern, timeless, and practical wardrobe.",
    Visual: StylistAnalysisStep,
  },
  {
    title: "Precision Guidance",
    body: "Your stylist delivers shopping suggestions, outfit formulas, and ongoing styling support tailored to your build and budget — you'll be getting a lot of compliments.",
    Visual: StyleGuidanceStep,
  },
];

/**
 * Scales a desktop-sized visual (laid out at 640×400) down to whatever width
 * its 16:10 box gets, so nothing inside reflows. The ratio has to be measured:
 * CSS can't divide a container-query length by a number to feed `scale()`.
 */
const VISUAL_W = 640;
const VISUAL_H = 400;
const FitVisual = ({ children }: { children: ReactNode }) => {
  const boxRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.47);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / VISUAL_W));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={boxRef} className="-ml-11 aspect-[16/10] overflow-hidden rounded-xl bg-[#FFF1E4]">
      <div
        className="origin-top-left"
        style={{ width: VISUAL_W, height: VISUAL_H, transform: `scale(${scale})` }}
      >
        {children}
      </div>
    </div>
  );
};

interface HowItWorksBassProps {
  /** Splash pages: below md, render the card accordion; adds the #how-it-works wrapper. */
  mobileRedesign?: boolean;
}

const HowItWorksBass = ({ mobileRedesign = false }: HowItWorksBassProps = {}) => {
  // First step (Digitize) open by default.
  const [open, setOpen] = useState<number>(0);
  const reduce = useReducedMotion();

  // Mobile: kicker + heading + a white card accordion. Each open step shows its
  // body and the same animated Visual as desktop, scaled into a 16:10 box.
  // No logo marquee here — schools/press live in the proof section above.
  const mobile = (
    <section className="md:hidden bg-[#FFF7F0] px-6 py-[72px]">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#FF8B11]">The Blueprint</p>
      <h2 className="mb-7 font-playfair text-[34px] font-bold leading-[1.1] text-[#0f172a]">
        How we build a practical, modern wardrobe.
      </h2>
      <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_0_rgba(15,23,42,0.06),0_16px_40px_-24px_rgba(15,23,42,0.3)]">
        {STEPS.map((step, i) => {
          const isOpen = open === i;
          const panelId = `hiw-m-panel-${i}`;
          const Visual = step.Visual;
          return (
            <div key={step.title} className="border-t border-[#f1ede7] first:border-t-0">
              <button
                type="button"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="flex min-h-[64px] w-full items-center gap-4 p-5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8B11]"
              >
                <span className="w-7 shrink-0 font-playfair text-[24px] italic leading-none text-[#FF8B11]">{i + 1}</span>
                <span className="flex-1 text-[16px] font-bold text-[#0f172a]">{step.title}</span>
                <span aria-hidden="true" className="shrink-0 font-playfair text-[24px] leading-none text-[#0f172a]">
                  {isOpen ? "−" : "+"}
                </span>
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    id={panelId}
                    key="panel"
                    initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                    animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
                    exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="pb-5 pl-16 pr-5">
                      <p className="mb-4 text-[14px] leading-[1.65] text-[#334155]">{step.body}</p>
                      {/* 16:10 box, pulled left to align with the step number. */}
                      <FitVisual>
                        <Visual autoplay />
                      </FitVisual>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </section>
  );

  const desktop = (
    <section id={mobileRedesign ? undefined : "how-it-works"} className="bg-white py-24 sm:py-32 px-4 sm:px-8 md:px-16 overflow-hidden relative">
      <div className="max-w-[1400px] mx-auto">

        {/* Pedigree Logo Wall — kept as-is */}
        <div className="mb-16 sm:mb-20 md:mb-24 py-8 sm:py-10 bg-white border-y border-gray-100 relative overflow-hidden flex flex-col gap-6 sm:gap-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-full bg-[#022c22]/[0.02] blur-3xl rounded-full opacity-50 pointer-events-none"></div>
          <div className="absolute left-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 sm:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

          {/* Row 1: Education */}
          <motion.div
            className="flex items-center w-max"
            animate={{ x: ["0%", "-50%"] }}
            transition={{ repeat: Infinity, ease: "linear", duration: 40 }}
          >
            {[1, 2].map((key) => (
              <div key={key} className="flex items-center justify-around gap-12 sm:gap-20 px-6 sm:px-10 min-w-max">
                <div className="flex items-center gap-4 sm:gap-6 text-gray-400 text-xs sm:text-sm font-semibold font-jakarta uppercase tracking-[0.25em] whitespace-nowrap">
                  <span>Partner with experienced stylists trained at</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#022c22]/30"></div>
                </div>
                <div className="flex items-center gap-8 sm:gap-14">
                  <img src={parsonsLogo} alt="Parsons School" className="h-6 sm:h-8 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                  <img src={fitLogo} alt="FIT" className="h-6 sm:h-8 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                  <img src={academyArtsLogo} alt="Academy of Arts" className="h-6 sm:h-8 object-contain invert grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                  <img src={scadLogo} alt="SCAD" className="h-6 sm:h-8 object-contain grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300" />
                </div>
              </div>
            ))}
          </motion.div>

          {/* Row 2: Publications */}
          <motion.div
            className="flex items-center w-max"
            animate={{ x: ["-50%", "0%"] }}
            transition={{ repeat: Infinity, ease: "linear", duration: 35 }}
          >
            {[1, 2].map((key) => (
              <div key={key} className="flex items-center justify-around gap-12 sm:gap-20 px-6 sm:px-10 min-w-max">
                <div className="flex items-center gap-4 sm:gap-6 text-gray-400 text-xs sm:text-sm font-semibold font-jakarta uppercase tracking-[0.25em] whitespace-nowrap">
                  <span>With industry experience and features in</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-[#022c22]/30"></div>
                </div>
                <div className="flex flex-row items-center gap-8 sm:gap-14">
                  <span className="font-playfair text-xl sm:text-2xl tracking-[0.15em] text-[#022c22] uppercase grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300">Elle</span>
                  <span className="font-playfair text-xl sm:text-2xl tracking-[0.2em] text-[#022c22] uppercase grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300">Vogue</span>
                  <span className="font-playfair text-lg sm:text-xl tracking-widest text-[#022c22] uppercase grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300 whitespace-nowrap">Vanity Fair</span>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* Pitch (left) + step accordion (right) */}
        <div className="grid items-start gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:gap-[72px]">
          {/* Left: the pitch — heading unchanged */}
          <div className="flex flex-col gap-5 lg:sticky lg:top-28">
            <p className="text-[#0f172a] font-bold tracking-[0.2em] uppercase text-[10px] sm:text-[11px]">
              The Blueprint
            </p>
            <h2 className="text-[#0f172a] text-[32px] sm:text-[40px] md:text-[48px] font-bold font-playfair leading-[1.05] tracking-tight">
              How we build a practical, modern wardrobe.
            </h2>
          </div>

          {/* Right: accordion */}
          <div>
            {STEPS.map((step, i) => {
              const isOpen = open === i;
              const panelId = `hiw-panel-${i}`;
              const Visual = step.Visual;
              return (
                <div key={step.title} className="border-b border-[#e2d8ce] first:border-t">
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="flex w-full items-center gap-5 sm:gap-6 py-[22px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FF8B11] focus-visible:ring-offset-2"
                  >
                    <span className="w-8 shrink-0 font-playfair text-[24px] sm:text-[26px] italic text-[#FF8B11]">
                      {i + 1}
                    </span>
                    <span className="flex-1 text-[16px] sm:text-[18px] font-bold text-[#0f172a] tracking-tight">
                      {step.title}
                    </span>
                    {/* Typographic toggle — no icons (brand rule) */}
                    <span aria-hidden="true" className="shrink-0 font-playfair text-2xl leading-none text-[#FF8B11]">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        id={panelId}
                        key="panel"
                        initial={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        animate={reduce ? { opacity: 1 } : { height: "auto", opacity: 1 }}
                        exit={reduce ? { opacity: 0 } : { height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="flex flex-col gap-5 pb-7 pl-[52px] sm:pl-[56px]">
                          <p className="max-w-[48ch] text-[#334155] text-[14px] leading-relaxed">
                            {step.body}
                          </p>
                          {/* Visual scaled to 70% to cut the vertical footprint ~30% */}
                          <div className="h-[280px] sm:h-[350px] overflow-hidden">
                            <div className="origin-top scale-[0.7]">
                              <Visual autoplay />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );

  if (!mobileRedesign) return desktop;

  // The anchor lives on this wrapper so the header's "How it works" link works
  // on both trees and lands below the mobile sticky group.
  return (
    <div id="how-it-works" className="scroll-mt-[var(--sticky-h,0px)] md:scroll-mt-0">
      {mobile}
      <div className="hidden md:block">{desktop}</div>
    </div>
  );
};

export default HowItWorksBass;
